import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api';
import type { Campaign } from '../types';
import {
  EmptyState,
  ErrorNotice,
  Loading,
  ProgressBar,
  Section,
  StatusPill,
  formatNzDateTime,
  numberValue,
  stringValue,
} from '../components/Common';

function ProgressRows({
  rows,
  nameField = 'name',
}: {
  rows: Array<Record<string, unknown>>;
  nameField?: string;
}) {
  if (rows.length === 0) {
    return <EmptyState title="No progress data">Progress will appear after setup.</EmptyState>;
  }
  return (
    <div className="progress-list">
      {rows.map((row, index) => (
        <ProgressBar
          key={stringValue(row.id) || `${stringValue(row[nameField])}-${index}`}
          value={numberValue(row.visited ?? row.completed)}
          max={numberValue(row.total ?? row.planned)}
          label={stringValue(row[nameField]) || 'Unspecified'}
          compact
        />
      ))}
    </div>
  );
}

export function DashboardPage({ campaign }: { campaign: Campaign }) {
  const [campusId, setCampusId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const dashboard = useQuery({
    queryKey: ['dashboard', campaign.id, campusId, buildingId],
    queryFn: () => apiClient.dashboard(campaign.id, campusId || undefined, buildingId || undefined),
  });
  if (dashboard.isLoading) return <Loading label="Calculating progress…" />;
  if (dashboard.error) return <ErrorNotice error={dashboard.error} />;
  const data = dashboard.data?.data;
  if (!data) return null;
  const metrics = data.metrics;
  const campusOptions = data.campuses;
  return (
    <div className="page dashboard-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Operations overview</p>
          <h1>Campaign progress</h1>
        </div>
        <div className="button-row">
          <label className="compact-select">
            Campus
            <select
              value={campusId}
              onChange={(event) => {
                setCampusId(event.target.value);
                setBuildingId('');
              }}
            >
              <option value="">All campuses</option>
              {campusOptions.map((campus) => (
                <option value={stringValue(campus.id)} key={stringValue(campus.id)}>
                  {stringValue(campus.name)}
                </option>
              ))}
            </select>
          </label>
          <label className="compact-select">
            Building
            <select value={buildingId} onChange={(event) => setBuildingId(event.target.value)}>
              <option value="">All buildings</option>
              {data.buildings.map((building) => (
                <option value={stringValue(building.id)} key={stringValue(building.id)}>
                  {stringValue(building.name)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>
      {metrics.inventorySetupRequired ? (
        <div className="notice notice-warning" role="alert">
          <strong>Initial flyer stock is not confirmed</strong>
          <span>
            An administrator must enter the exact printed quantity before stock is issued.
          </span>
        </div>
      ) : null}
      <section className="metric-grid" aria-label="Headline metrics">
        <article className="metric-card metric-primary">
          <span>Flyers distributed</span>
          <strong>{metrics.flyersDistributed.toLocaleString('en-NZ')}</strong>
          <small>From the immutable visit ledger</small>
        </article>
        <article className="metric-card">
          <span>Stock remaining</span>
          <strong>{metrics.stockRemaining.toLocaleString('en-NZ')}</strong>
          <small>{metrics.centralStock.toLocaleString('en-NZ')} in central stock</small>
        </article>
        <article className="metric-card">
          <span>Locations visited</span>
          <strong>
            {metrics.uniqueLocationsVisited}/{metrics.totalLocations}
          </strong>
          <small>{metrics.completionPercent}% complete</small>
        </article>
        <article className="metric-card">
          <span>Total visit events</span>
          <strong>{metrics.totalVisitEvents}</strong>
          <small>Repeat visits are retained</small>
        </article>
        <article className="metric-card metric-warning">
          <span>Due for revisit</span>
          <strong>{metrics.dueLocations}</strong>
          <small>Based on configured intervals</small>
        </article>
      </section>
      <div className="dashboard-columns">
        <Section title="Completion by wave">
          <ProgressRows rows={data.waves} />
        </Section>
        <Section title="Completion by campus">
          <ProgressRows rows={data.campuses} />
        </Section>
        <Section title="Completion by building">
          <ProgressRows rows={data.buildings} />
        </Section>
        <Section title="Completion by location type">
          <ProgressRows rows={data.locationTypes} />
        </Section>
      </div>
      <div className="dashboard-columns">
        <Section
          title="Due for revisit"
          action={<StatusPill tone="warning">{data.dueLocations.length}</StatusPill>}
        >
          {data.dueLocations.length ? (
            <ul className="operational-list">
              {data.dueLocations.map((location) => (
                <li key={stringValue(location.id)}>
                  <div>
                    <strong>{stringValue(location.room_code)}</strong>
                    <span>
                      {stringValue(location.campus_name)} · {stringValue(location.building_name)}
                    </span>
                  </div>
                  <span>{formatNzDateTime(stringValue(location.next_due_at))}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Nothing due">No approved location is currently due.</EmptyState>
          )}
        </Section>
        <Section
          title="Unvisited high priority"
          action={<StatusPill tone="danger">{data.unvisitedHighPriority.length}</StatusPill>}
        >
          {data.unvisitedHighPriority.length ? (
            <ul className="operational-list">
              {data.unvisitedHighPriority.map((location) => (
                <li key={stringValue(location.id)}>
                  <div>
                    <strong>{stringValue(location.room_code)}</strong>
                    <span>
                      {stringValue(location.campus_name)} · {stringValue(location.building_name)}
                    </span>
                  </div>
                  <StatusPill tone="warning">Priority {numberValue(location.priority)}</StatusPill>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="All covered">No high-priority location is unvisited.</EmptyState>
          )}
        </Section>
        <Section
          title="Access and permission problems"
          action={<StatusPill tone="danger">{data.accessProblems.length}</StatusPill>}
        >
          {data.accessProblems.length ? (
            <ul className="operational-list">
              {data.accessProblems.map((location) => (
                <li key={stringValue(location.id)}>
                  <div>
                    <strong>{stringValue(location.room_code)}</strong>
                    <span>
                      {stringValue(location.building_name)} ·{' '}
                      {stringValue(
                        location.last_access_outcome ?? location.permission_status,
                      ).replaceAll('_', ' ')}
                    </span>
                  </div>
                  <StatusPill tone="danger">Review</StatusPill>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No problems recorded">
              Access issues will be surfaced here without encouraging entry.
            </EmptyState>
          )}
        </Section>
        <Section
          title="Inventory reconciliation"
          action={<StatusPill tone="warning">{data.inventoryWarnings.length}</StatusPill>}
        >
          {data.inventoryWarnings.length ? (
            <ul className="operational-list">
              {data.inventoryWarnings.map((warning) => (
                <li key={`${stringValue(warning.warning_type)}-${stringValue(warning.id)}`}>
                  <div>
                    <strong>{stringValue(warning.warning_type).replaceAll('_', ' ')}</strong>
                    <span>{stringValue(warning.message)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Stock reconciles">No inventory warning is open.</EmptyState>
          )}
        </Section>
      </div>
      <div className="dashboard-columns">
        <Section
          title="Active and recent runs"
          action={<StatusPill tone="info">{data.runs.length}</StatusPill>}
        >
          {data.runs.length ? (
            <ul className="operational-list">
              {data.runs.map((run) => (
                <li key={stringValue(run.id)}>
                  <div>
                    <strong>{stringValue(run.name)}</strong>
                    <span>
                      {stringValue(run.wave_name)} · {stringValue(run.leader_name)} ·{' '}
                      {numberValue(run.participant_count)} participant
                      {numberValue(run.participant_count) === 1 ? '' : 's'}
                    </span>
                  </div>
                  <StatusPill
                    tone={
                      run.status === 'active'
                        ? 'success'
                        : run.status === 'paused'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {stringValue(run.status)}
                  </StatusPill>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No runs yet">
              Planned and active distribution runs will appear here.
            </EmptyState>
          )}
        </Section>
        <Section
          title="Volunteer activity"
          action={<StatusPill tone="info">{data.volunteers.length}</StatusPill>}
        >
          {data.volunteers.length ? (
            <ul className="operational-list">
              {data.volunteers.map((volunteer) => (
                <li key={stringValue(volunteer.id)}>
                  <div>
                    <strong>{stringValue(volunteer.display_name)}</strong>
                    <span>
                      Last active {formatNzDateTime(stringValue(volunteer.last_activity_at))}
                    </span>
                  </div>
                  <span>
                    {numberValue(volunteer.visits)} visits ·{' '}
                    {numberValue(volunteer.flyers_recorded)} flyers
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No volunteer activity">
              Participant history will appear after the first visit.
            </EmptyState>
          )}
        </Section>
      </div>
      <Section title="Recent event timeline">
        {data.timeline.length ? (
          <ol className="timeline">
            {data.timeline.map((event) => (
              <li key={stringValue(event.id)}>
                <span
                  className={
                    event.possible_duplicate_of_event_id ? 'timeline-dot warning' : 'timeline-dot'
                  }
                  aria-hidden="true"
                />
                <div>
                  <strong>
                    {stringValue(event.action_type).replaceAll('_', ' ')} ·{' '}
                    {stringValue(event.room_code)}
                  </strong>
                  <span>
                    {stringValue(event.volunteer_name)} · {stringValue(event.building_name)} ·{' '}
                    {formatNzDateTime(stringValue(event.server_received_at))}
                  </span>
                  {event.possible_duplicate_of_event_id ? (
                    <StatusPill tone="warning">Possible duplicate</StatusPill>
                  ) : null}
                </div>
                <strong>{numberValue(event.flyer_quantity)}</strong>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState title="No visits yet">
            Recorded visits will appear here in server receipt order.
          </EmptyState>
        )}
      </Section>
    </div>
  );
}
